import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

const card = { background: 'white', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '16px', boxShadow: '0 12px 30px rgba(24,47,38,0.08)' };
const muted = { color: '#61726b', fontSize: '13px' };

function euro(value) {
  return Number(value || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function Pill({ tone = 'slate', children }) {
  const colors = {
    green: ['#dff3e9', '#064734', '#a7d9c2'],
    amber: ['#fff7d6', '#8a5a00', '#f4d36b'],
    red: ['#fff1ef', '#9f1d12', '#ffc9c2'],
    blue: ['#e8f0ff', '#23417a', '#bfd0ff'],
    slate: ['#eef4f1', '#334155', '#d8e4dd'],
  };
  const [bg, color, border] = colors[tone] || colors.slate;
  return <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: '999px', padding: '4px 9px', fontSize: '11px', fontWeight: 900 }}>{children}</span>;
}

export default function EmpfaengerPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const empfaenger = params.get('empfaenger') || '';
  const kategorie = params.get('kategorie') || 'Unkategorisiert';
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [ruleTerm, setRuleTerm] = useState('');
  const [ruleCategory, setRuleCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [rulePreview, setRulePreview] = useState(null);
  const [lastApplicationId, setLastApplicationId] = useState(null);

  useEffect(() => { load(); loadCategories(); }, [empfaenger, kategorie]);
  useEffect(() => { previewRule(); }, [ruleTerm]);

  async function load() {
    if (!empfaenger) return;
    setError('');
    try {
      const r = await api.get('/empfaenger/detail', { params: { empfaenger, kategorie } });
      setData(r.data);
      setRuleTerm(prev => prev || (r.data.empfaenger || '').toUpperCase().slice(0, 20));
      setRuleCategory(prev => prev || r.data.kategorie || 'Sonstiges');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Empfaenger konnte nicht geladen werden');
    }
  }

  async function loadCategories() {
    try {
      const r = await api.get('/kategorien');
      setCategories(r.data || []);
    } catch (e) {
      setCategories([]);
    }
  }

  async function previewRule() {
    if (!ruleTerm || ruleTerm.length < 3) {
      setRulePreview(null);
      return;
    }
    try {
      const r = await api.get('/empfaenger/detail/rule-preview', { params: { suchbegriff: ruleTerm } });
      setRulePreview(r.data);
    } catch (e) {
      setRulePreview(null);
    }
  }

  async function save(patch) {
    if (!data?.summary) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        empfaenger: data.empfaenger,
        kategorie: data.kategorie,
        ist_geldfresser: data.summary.ist_geldfresser,
        ist_ueberfluessig: data.summary.ist_ueberfluessig,
        muss_kuendigen: data.summary.muss_kuendigen,
        ist_notwendig: data.flags?.ist_notwendig || 0,
        user_notiz: data.summary.user_notiz || '',
        ...patch
      };
      // Mutual exclusion: last click wins — Geldfresser bleibt unabhängig
      if (patch.muss_kuendigen)    payload.ist_notwendig = 0;
      if (patch.ist_ueberfluessig) payload.ist_notwendig = 0;
      if (patch.ist_notwendig)     { payload.muss_kuendigen = 0; payload.ist_ueberfluessig = 0; }
      const r = await api.post('/empfaenger/detail/flags', payload);
      setMessage(`${r.data.updated} Buchungen aktualisiert.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Bewertung konnte nicht gespeichert werden');
    } finally {
      setBusy(false);
    }
  }

  async function saveRule() {
    if (!data) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await api.post('/empfaenger/detail/rule', {
        empfaenger: data.empfaenger,
        suchbegriff: ruleTerm,
        kategorie: ruleCategory,
        anwenden: true
      });
      setLastApplicationId(r.data.applicationId || null);
      setMessage(`${r.data.created ? 'Regel erstellt' : 'Regel aktualisiert'}; ${r.data.updated} Buchungen angewendet.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Regel konnte nicht gespeichert werden');
    } finally {
      setBusy(false);
    }
  }

  async function undoLastRule() {
    if (!lastApplicationId) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await api.post(`/rules/applications/${lastApplicationId}/undo`);
      setMessage(`${r.data.restored} Buchungen zurückgesetzt.`);
      setLastApplicationId(null);
      await load();
      await previewRule();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Undo fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  const yearly = data?.summary?.jahreskosten || 0;
  const avgMonthly = useMemo(() => {
    const months = data?.monthly || [];
    if (!months.length) return 0;
    return months.reduce((sum, item) => sum + Number(item.ausgaben || 0), 0) / months.length;
  }, [data]);

  if (!empfaenger) return <div style={card}>Kein Empfaenger ausgewaehlt.</div>;
  if (!data && !error) return <div style={card}>Empfaenger wird geladen...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <button onClick={() => nav('/geldfresser')} style={linkButton}>Zurück zu Geldfressern</button>
          <h1 style={{ margin: '6px 0 0', color: '#064734', fontSize: '25px' }}>{empfaenger}</h1>
          <p style={{ ...muted, margin: '4px 0 0' }}>{kategorie}</p>
        </div>
        <Pill tone={data?.summary?.muss_kuendigen ? 'red' : data?.summary?.ist_geldfresser ? 'amber' : 'slate'}>
          {data?.summary?.muss_kuendigen ? 'Kündigung' : data?.summary?.ist_geldfresser ? 'Geldfresser' : 'offen'}
        </Pill>
      </div>

      {error && <Notice tone="red">{error}</Notice>}
      {message && <Notice tone="green">{message}</Notice>}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <Stat title="Ausgaben" value={euro(data.summary?.ausgaben)} hint={`${data.summary?.anzahl || 0} Buchungen`} />
            <Stat title="Durchschnitt" value={euro(data.summary?.durchschnitt)} hint="pro Abbuchung" />
            <Stat title="Monatlich" value={euro(avgMonthly)} hint="Durchschnitt" />
            <Stat title="Jahr" value={euro(yearly)} hint="hochgerechnet" />
          </div>

          <section style={{ ...card, marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={h2}>Bewertung</h2>
              <Pill tone="blue">{data.summary?.monate || 0} Monate</Pill>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '10px' }}>
              <Check label="Geldfresser" checked={!!data.summary?.ist_geldfresser} disabled={busy} onChange={v => save({ ist_geldfresser: v })} />
              <Check label="Notwendig" checked={!!data.flags?.ist_notwendig} disabled={busy} onChange={v => save({ ist_notwendig: v })} />
              <Check label="Überflüssig" checked={!!data.summary?.ist_ueberfluessig} disabled={busy} onChange={v => save({ ist_ueberfluessig: v })} />
              <Check label="Kündigen" checked={!!data.summary?.muss_kuendigen} disabled={busy} onChange={v => save({ muss_kuendigen: v })} />
            </div>
            <textarea
              value={data.summary?.user_notiz || ''}
              onChange={e => setData({ ...data, summary: { ...data.summary, user_notiz: e.target.value } })}
              onBlur={e => save({ user_notiz: e.target.value })}
              placeholder="Notiz, z.B. Vertrag prüfen, behalten, Alternative suchen..."
              style={{ width: '100%', minHeight: '76px', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px', color: '#12231d' }}
            />
          </section>

          <section style={{ ...card, marginBottom: '12px' }}>
            <h2 style={h2}>Monatsverlauf</h2>
            <div style={{ display: 'grid', gap: '7px', marginTop: '10px' }}>
              {data.monthly.map(row => (
                <div key={row.monat} style={rowStyle}>
                  <strong>{row.monat}</strong>
                  <span>{row.anzahl} Buchungen</span>
                  <strong style={{ color: '#b42318' }}>{euro(row.ausgaben)}</strong>
                </div>
              ))}
            </div>
          </section>

          <section style={{ ...card, marginBottom: '12px' }}>
            <h2 style={h2}>Letzte Buchungen</h2>
            <div style={{ display: 'grid', gap: '7px', marginTop: '10px' }}>
              {data.buchungen.slice(0, 25).map(b => (
                <div key={b.id} style={rowStyle}>
                  <div>
                    <strong>{b.datum}</strong>
                    <div style={muted}>{b.verwendungszweck || b.empfaenger}</div>
                  </div>
                  <strong style={{ color: Number(b.betrag) < 0 ? '#b42318' : '#0a6f4f' }}>{euro(b.betrag)}</strong>
                </div>
              ))}
            </div>
          </section>


          <section style={{ ...card, marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={h2}>Regel-Assistent</h2>
              <Pill tone="green">dauerhaft lernen</Pill>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
              <label style={{ display: 'grid', gap: '4px', color: '#61726b', fontSize: '12px', fontWeight: 900 }}>
                Suchbegriff
                <input value={ruleTerm} onChange={e => setRuleTerm(e.target.value.toUpperCase())} style={inputStyle} />
              </label>
              <label style={{ display: 'grid', gap: '4px', color: '#61726b', fontSize: '12px', fontWeight: 900 }}>
                Kategorie
                <select value={ruleCategory} onChange={e => setRuleCategory(e.target.value)} style={inputStyle}>
                  {categories.map(c => <option key={c.name || c} value={c.name || c}>{c.name || c}</option>)}
                  {!categories.find(c => (c.name || c) === ruleCategory) && <option value={ruleCategory}>{ruleCategory}</option>}
                </select>
              </label>
              <button onClick={saveRule} disabled={busy || !ruleTerm || !ruleCategory || !rulePreview?.total} style={primaryButton}>Regel speichern</button>
            </div>
            <div style={{ marginTop: '10px', border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px', background: '#f7fbf8' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'center' }}>
                <strong style={{ color: '#064734' }}>Vorschau</strong>
                <Pill tone={rulePreview?.total ? 'green' : 'amber'}>{rulePreview?.total || 0} Treffer</Pill>
              </div>
              {rulePreview?.examples?.length ? (
                <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                  {rulePreview.examples.slice(0, 5).map(b => (
                    <div key={b.id} style={miniRow}>{b.datum} · {b.empfaenger} · {euro(b.betrag)} · {b.kategorie || 'ohne Kategorie'}</div>
                  ))}
                </div>
              ) : <p style={{ ...muted, margin: '8px 0 0' }}>Suchbegriff eingeben, um betroffene Buchungen zu sehen.</p>}
            </div>
            <p style={{ ...muted, margin: '8px 0 0' }}>Speichert eine aktive Regel und wendet sie erst nach Klick auf die angezeigten Treffer an.</p>
            {lastApplicationId && <button onClick={undoLastRule} disabled={busy} style={{ ...linkButton, marginTop: '10px', borderColor: '#ffc9c2', color: '#9f1d12' }}>Letzte Regelanwendung rückgängig</button>}
          </section>

          <section style={{ ...card, marginBottom: '12px' }}>
            <h2 style={h2}>Regeln und aehnliche Empfaenger</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}>
              <div>
                <h3 style={h3}>Passende Regeln</h3>
                {data.rules.length ? data.rules.map(r => <div key={r.id} style={miniRow}>{r.suchbegriff} -> {r.kategorie}</div>) : <Empty text="Keine passende Regel gefunden." />}
              </div>
              <div>
                <h3 style={h3}>Aehnliche Empfaenger</h3>
                {data.similar.length ? data.similar.map(s => <div key={s.empfaenger} style={miniRow}>{s.empfaenger}: {euro(s.gesamt)}</div>) : <Empty text="Keine aehnlichen Empfaenger." />}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Check({ label, checked, disabled, onChange }) {
  return (
    <label style={{ border: '1px solid #d8e4dd', borderRadius: '8px', padding: '9px 10px', display: 'flex', gap: '8px', alignItems: 'center', background: checked ? '#dff3e9' : '#fff', color: '#12231d', fontWeight: 900 }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Stat({ title, value, hint }) {
  return <div style={card}><div style={{ color: '#61726b', fontSize: '11px', fontWeight: 900, textTransform: 'uppercase' }}>{title}</div><div style={{ color: '#064734', fontSize: '23px', fontWeight: 950, marginTop: '6px' }}>{value}</div><div style={{ ...muted, marginTop: '3px' }}>{hint}</div></div>;
}

function Notice({ tone, children }) {
  const red = tone === 'red';
  return <div style={{ background: red ? '#fff1ef' : '#dff3e9', color: red ? '#9f1d12' : '#064734', border: `1px solid ${red ? '#ffc9c2' : '#a7d9c2'}`, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontWeight: 800 }}>{children}</div>;
}

function Empty({ text }) {
  return <div style={{ ...muted, padding: '8px 0' }}>{text}</div>;
}

const h2 = { margin: 0, color: '#064734', fontSize: '17px' };
const h3 = { margin: '0 0 8px', color: '#12231d', fontSize: '14px' };
const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', borderTop: '1px solid #eef4f1', padding: '8px 0' };
const miniRow = { borderTop: '1px solid #eef4f1', padding: '7px 0', color: '#334155', fontSize: '13px' };
const linkButton = { border: '1px solid #d8e4dd', background: '#fff', color: '#064734', borderRadius: '8px', padding: '7px 10px', fontWeight: 900, cursor: 'pointer' };
const inputStyle = { border: '1px solid #d8e4dd', borderRadius: '8px', padding: '10px', color: '#12231d', fontWeight: 800 };
const primaryButton = { border: 'none', background: '#0a6f4f', color: 'white', borderRadius: '8px', padding: '11px 13px', fontWeight: 900, cursor: 'pointer' };
